import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { MismatchReason } from '@designdeck/shared';
import { Button } from '@/components/ui';

interface MissingPositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  /** Human label of the Position in question, e.g. "Rücken". */
  positionLabel: string;
  /** Why the product cannot carry the motif; a missing Position by default. */
  reason?: MismatchReason;
}

/**
 * Shown when the seller tries to select a product that cannot carry a motif
 * they placed — no Position for that side, or a Position that is only printed
 * where the motif is embroidered (and the other way round). Until now the
 * motif was dropped silently on save, or the design failed at publish; this
 * says so before the pick.
 *
 * Same shape and tone as DecorationMethodConflictModal.
 */
export function MissingPositionModal({
  isOpen,
  onClose,
  productName,
  positionLabel,
  reason = 'missing-position',
}: MissingPositionModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const copy = copyFor(t, reason, productName, positionLabel);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="missing-position-title"
        aria-describedby="missing-position-description"
        className="relative w-full max-w-md bg-paper-2 rounded-section ring-1 ring-line overflow-hidden"
      >
        <div className="px-6 py-5">
          <h3
            id="missing-position-title"
            className="text-lg font-semibold text-ink"
          >
            {copy.title}
          </h3>
          <p
            id="missing-position-description"
            className="text-sm text-ink-2 mt-2 leading-relaxed"
          >
            {copy.body}
          </p>
        </div>
        <div className="px-6 py-4 border-t border-line bg-paper flex justify-end">
          <Button variant="primary" onClick={onClose}>
            {t('creator.modals.understood')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function copyFor(
  t: TFunction,
  reason: MismatchReason,
  productName: string,
  positionLabel: string
): { title: string; body: string } {
  const values = { product: productName, position: positionLabel };
  switch (reason) {
    case 'not-print':
      return {
        title: t('creator.modals.notPrintTitle', values),
        body: t('creator.modals.notPrintBody', values),
      };
    case 'not-embroidery':
      return {
        title: t('creator.modals.notEmbroideryTitle', values),
        body: t('creator.modals.notEmbroideryBody', values),
      };
    case 'too-narrow':
    case 'missing-position':
    default:
      return {
        title: t('creator.modals.missingPositionTitle', values),
        body: t('creator.modals.missingPositionBody', values),
      };
  }
}
